<script setup lang="ts">
import {ref, watch} from "vue";
import {converterCurrencies, getSignFromQuote, supportedCryptoQuoteCurrencies} from "@consts";
import type {ConverterRateResponse} from "@lib/interfaces/converter.ts";
import PrimaryButton from "@components/utils/PrimaryButton/PrimaryButton.vue";
import {formatCurrency} from "@lib/formatCurrencyNumbers.ts";

import {useI18n} from "vue-i18n";
const {locale} = useI18n({
  useScope: 'global'
});


const props = defineProps({
  crypto: {
    type: String,
    required: true
  },
  quote: {
    type: String,
    required: true
  },
  converterRate: {
    type: Object as () => ConverterRateResponse,
    required: true
  }
});

const cryptoAmount = ref<number>(1);
const currencyAmount = ref<number|undefined>(props?.converterRate?.conversions?.[0]?.total);
const conversionRate = props?.converterRate?.conversions?.[0]?.rate;
const sign = getSignFromQuote(props.quote)

const otherConvertCurrencies = [
    ...converterCurrencies.map(cur => cur.label),
    ...supportedCryptoQuoteCurrencies,
]

watch(() => cryptoAmount.value, (value) => {
  if(!conversionRate) return;
  currencyAmount.value = Number.parseFloat((value * conversionRate).toFixed(2))
});
// watch(() => currencyAmount.value, (value) => {
//   if(!conversionRate || !value) return;
//   // TODO: problem of floating point precision
//   cryptoAmount.value = Number.parseFloat((value / conversionRate).toFixed(2))
// });

const toConverter = (quote: string) => {
  // TODO: add locale of current lang
  return `/${locale?.value?.slice(0,2) ?? 'en'}/converter/${props.crypto?.toLowerCase()}-to-${quote?.toLowerCase()}`.toLowerCase();
};
</script>
<template>
  <div
      id="price-calculator"
      class="flex flex-col items-center relative z-2 isolate"
  >
    <div
        class="w-full flex flex-col lt-sm:(px-2 pb-6 pt-2) sm:p-6 gap-6 bg-brand-background/90 relative border-brand-stroke/10 border-2 rounded-4"
    >
      <h2 class="text-lg font-semibold text-white text-center mb-4">
        Calculator from {{ crypto }} to {{ quote }}
      </h2>

      <div class="converter-box flex flex-col gap-6">
        <div class="input-group flex flex-col gap-2">
          <label
              for="crypto"
              class="text-brand-secondary text-sm font-semibold"
          >
            Crypto
          </label>
          <div class="flex items-center bg-brand-background/80 border border-brand-stroke/10 rounded-4 p-2">
            <input
                type="number"
                id="crypto"
                v-model="cryptoAmount"
                class="flex-1 bg-transparent text-white placeholder-brand-secondary outline-none"
            />
            <span class="ml-2 text-brand-primary font-semibold">
              {{ crypto }}
            </span>
          </div>
        </div>

        <div class="input-group flex flex-col gap-2">
          <label
              for="currency"
              class="text-brand-secondary text-sm font-semibold"
          >
            Quote
          </label>
          <div class="flex items-center bg-brand-background/80 border border-brand-stroke/10 rounded-4 p-2">
            <input
                type="number"
                id="currency"
                v-model="currencyAmount"
                class="flex-1 bg-transparent text-white placeholder-brand-secondary outline-none"
            />
            <span class="ml-2 text-brand-primary font-semibold">
              {{ quote }}
            </span>
          </div>
        </div>

        <div class="currency-options gap-3 justify-center grid grid-cols-6">
          <a
              v-for="cur of otherConvertCurrencies"
              :href="toConverter(cur)"
              class="text-brand-primary hover:text-brand-secondary font-semibold"
          >
            {{ cur }}
          </a>
        </div>

        <div class="conversion-rate text-center text-white mt-4">
          <p class="text-lg font-semibold">
            {{cryptoAmount}} {{ crypto }} = {{ formatCurrency((conversionRate??0) * cryptoAmount, sign) }} {{ quote }}
          </p>
        </div>

        <PrimaryButton
            href="https://app.dipsway.com"
        >
          Trade on {{ crypto }}
        </PrimaryButton>
      </div>
    </div>
  </div>
</template>